<script setup>
import { ref, computed, onMounted } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import appointmentsAPI from 'dashboard/api/appointments';
import googleCalendarAPI from 'dashboard/api/googleCalendar';
import Spinner from 'shared/components/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();

const loading = ref(false);
const saving = ref(false);
const showForm = ref(false);
const editingAppointment = ref(null);
const appointments = ref([]);

const currentChat = useMapGetter('getSelectedChat');
const currentAccount = useMapGetter('getCurrentAccount');

// Provider padrão do link da reunião (definido nas configs da conta) + se o
// Google Calendar está conectado (Google Meet só é ofertado quando estiver).
const accountDefaultProvider = computed(
  () => currentAccount.value?.settings?.default_meeting_provider || 'chatsuite'
);
const isGoogleConnected = ref(false);

// Agentes da conta para o campo "Agendar para": o SDR qualifica e marca a
// reunião na agenda do closer. Vem com google_connected por agente, porque quem
// não conectou o Google fica só com o compromisso do ChatSuite.
const schedulableAgents = ref([]);
const currentUser = useMapGetter('getCurrentUser');

const form = ref({
  title: '',
  event_type: 'event',
  date: '',
  time: '',
  duration_minutes: 30,
  location: '',
  description: '',
  funnel_id: null,
  meeting_provider: 'chatsuite',
  assigned_to_id: null,
});

const selectedAgent = computed(() =>
  schedulableAgents.value.find(
    agent => Number(agent.id) === Number(form.value.assigned_to_id)
  )
);

// Aviso só quando escolheram alguém que não tem Google — sem isso a pessoa
// acha que o evento foi pra agenda dela e ele nunca aparece lá.
const assigneeWithoutGoogle = computed(
  () => selectedAgent.value && !selectedAgent.value.google_connected
);

const upcomingAppointments = computed(() => {
  return appointments.value.filter(
    apt =>
      new Date(apt.appointment_date || apt.scheduled_at) >= new Date() &&
      apt.status !== 'cancelled'
  );
});

const fetchAppointments = async () => {
  loading.value = true;
  try {
    const response = await appointmentsAPI.index(props.conversationId);
    appointments.value = response.data;
  } catch (error) {
    useAlert(t('APPOINTMENTS.API_ERROR.FETCH'));
  } finally {
    loading.value = false;
  }
};

// Computed para obter funis do store
const funnels = computed(() => {
  return store.getters['funnels/getFunnels'] || [];
});

// Função para obter o nome do funil pelo ID
const getFunnelName = funnelId => {
  const funnel = funnels.value.find(f => f.id === funnelId);
  return funnel ? funnel.name : '';
};

const loadingFunnels = computed(() => {
  return store.getters['funnels/isLoading'];
});

// Função para buscar funis disponíveis
const fetchFunnels = async () => {
  try {
    await store.dispatch('funnels/get');
  } catch (error) {
    console.error('Error fetching funnels:', error);
  }
};

// Função para obter o funnel_id atual
const getCurrentFunnelId = () => {
  // Busca o funnel_id da conversa atual
  if (!currentChat.value) return null;
  // Verifica se é a conversa correta
  if (currentChat.value.id !== Number(props.conversationId)) return null;
  const conversation = currentChat.value;
  if (conversation?.funnel_mappings?.length > 0) {
    return conversation.funnel_mappings[0].funnel_id;
  }
  return null;
};

// Lead já está num funil → o agendamento usa esse funil sozinho e o campo
// de seleção nem aparece (só faz sentido escolher quando não há funil).
const conversationFunnelId = computed(() => getCurrentFunnelId());

const resetForm = () => {
  form.value = {
    title: '',
    event_type: 'event',
    date: '',
    time: '',
    duration_minutes: 30,
    location: '',
    description: '',
    funnel_id: getCurrentFunnelId(),
    meeting_provider: accountDefaultProvider.value,
    assigned_to_id: currentUser.value?.id || null,
  };
  editingAppointment.value = null;
};

const toggleForm = () => {
  showForm.value = !showForm.value;
  if (showForm.value && !editingAppointment.value) {
    form.value.meeting_provider = accountDefaultProvider.value;
  }
  if (!showForm.value) {
    try {
      resetForm();
    } catch (error) {
      console.error('Error in resetForm:', error);
    }
  }
};

const saveAppointment = async () => {
  if (!form.value.title || !form.value.date || !form.value.time) {
    useAlert(t('APPOINTMENTS.VALIDATION_ERROR'));
    return;
  }

  saving.value = true;
  try {
    const scheduled_at = new Date(`${form.value.date}T${form.value.time}`);
    const payload = {
      title: form.value.title,
      event_type: form.value.event_type,
      scheduled_at: scheduled_at.toISOString(),
      duration_minutes: parseInt(form.value.duration_minutes, 10),
      location: form.value.location,
      description: form.value.description,
      funnel_id: form.value.funnel_id
        ? parseInt(form.value.funnel_id, 10)
        : null,
      meeting_provider: form.value.meeting_provider,
      assigned_to_id: form.value.assigned_to_id
        ? parseInt(form.value.assigned_to_id, 10)
        : null,
    };

    if (editingAppointment.value) {
      await appointmentsAPI.update(
        props.conversationId,
        editingAppointment.value.id,
        payload
      );
      useAlert(t('APPOINTMENTS.API_SUCCESS.UPDATE'));
    } else {
      await appointmentsAPI.create(props.conversationId, payload);
      useAlert(t('APPOINTMENTS.API_SUCCESS.CREATE'));
    }

    await fetchAppointments();

    // Atualizar conversa no store para sincronizar com o Kanban
    updateConversationInStore();

    toggleForm();
  } catch (error) {
    console.error('Appointment save error:', error);
    useAlert(t('APPOINTMENTS.API_ERROR.SAVE'));
  } finally {
    saving.value = false;
  }
};

const editAppointment = appointment => {
  editingAppointment.value = appointment;
  const scheduledAt = parseISO(
    appointment.appointment_date || appointment.scheduled_at
  );
  form.value = {
    title: appointment.title,
    event_type: appointment.event_type || appointment.appointment_type,
    date: format(scheduledAt, 'yyyy-MM-dd'),
    time: format(scheduledAt, 'HH:mm'),
    duration_minutes: appointment.duration_minutes,
    location: appointment.location || '',
    description: appointment.description || '',
    funnel_id: appointment.funnel_id || getCurrentFunnelId(),
    meeting_provider: appointment.meeting_provider || 'chatsuite',
    assigned_to_id:
      appointment.assigned_to_id ||
      appointment.user_id ||
      currentUser.value?.id,
  };
  showForm.value = true;
};

const deleteAppointment = async appointment => {
  if (!confirm(t('APPOINTMENTS.CONFIRM_DELETE'))) return;

  try {
    await appointmentsAPI.destroy(props.conversationId, appointment.id);
    useAlert(t('APPOINTMENTS.API_SUCCESS.DELETE'));
    await fetchAppointments();

    // Atualizar conversa no store para sincronizar com o Kanban
    updateConversationInStore();
  } catch (error) {
    useAlert(t('APPOINTMENTS.API_ERROR.DELETE'));
  }
};

const formatDate = dateString => {
  if (!dateString) return '';
  return format(parseISO(dateString), 'dd/MM/yyyy HH:mm');
};

const getStatusLabel = status => {
  const labels = {
    scheduled: t('APPOINTMENTS.STATUS_SCHEDULED'),
    confirmed: t('APPOINTMENTS.STATUS_CONFIRMED'),
    completed: t('APPOINTMENTS.STATUS_COMPLETED'),
    cancelled: t('APPOINTMENTS.STATUS_CANCELLED'),
    no_show: t('APPOINTMENTS.STATUS_NO_SHOW'),
  };
  return labels[status] || status;
};

const getStatusClass = status => {
  const classes = {
    scheduled: 'bg-blue-100 text-blue-800',
    confirmed: 'bg-blue-100 text-blue-800',
    completed: 'bg-gray-100 text-gray-800',
    cancelled: 'bg-red-100 text-red-800',
    no_show: 'bg-yellow-100 text-yellow-800',
  };
  return classes[status] || 'bg-gray-100 text-gray-800';
};

// Função para atualizar a conversa no store Vuex
const updateConversationInStore = () => {
  // Buscar a conversa atual do store
  const allConversations = store.getters['conversations/getAllConversations'];
  const conversation = allConversations?.find(
    c => c.id === Number(props.conversationId)
  );

  if (conversation) {
    // Atualiza o campo conversation_appointments da conversa no store
    store.commit('conversations/UPDATE_CONVERSATION', {
      ...conversation,
      conversation_appointments: appointments.value,
    });
  }
};

const loadGoogleStatus = async () => {
  try {
    const { data } = await googleCalendarAPI.getStatus();
    isGoogleConnected.value = !!data.connected;
  } catch (error) {
    isGoogleConnected.value = false;
  }
};

const loadSchedulableAgents = async () => {
  try {
    const { data } = await googleCalendarAPI.listSchedulableAgents();
    schedulableAgents.value = data.users || [];
  } catch (error) {
    // Sem a lista o campo some e o agendamento continua indo pra quem criou —
    // degrada pro comportamento antigo em vez de travar o formulário.
    schedulableAgents.value = [];
  }
  if (!form.value.assigned_to_id) {
    form.value.assigned_to_id = currentUser.value?.id || null;
  }
};

onMounted(() => {
  fetchAppointments();
  fetchFunnels();
  loadGoogleStatus();
  loadSchedulableAgents();
});
</script>

<template>
  <div class="conversation-appointments">
    <!-- Loading State -->
    <div v-if="loading" class="p-4 flex justify-center">
      <Spinner />
    </div>

    <!-- Main Content -->
    <div v-else class="p-4">
      <!-- Header with Add Button -->
      <div class="flex justify-between items-center mb-4">
        <div class="flex items-center gap-3">
          <h4 class="text-sm font-semibold text-n-slate-12">Agendamentos</h4>
          <span
            v-if="upcomingAppointments.length > 0"
            class="text-xs text-n-slate-11 bg-n-alpha-2 px-2 py-0.5 rounded-full"
          >
            {{ upcomingAppointments.length }}
          </span>
        </div>
        <button
          class="p-1.5 text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2 rounded-lg transition-all"
          :title="showForm ? t('COMMON.CANCEL') : 'Adicionar Agendamento'"
          @click="toggleForm"
        >
          <fluent-icon :icon="showForm ? 'dismiss' : 'add'" size="16" />
        </button>
      </div>

      <!-- Form -->
      <div
        v-if="showForm"
        class="border border-n-weak bg-n-solid-2 rounded-xl p-4 mb-4"
      >
        <div class="space-y-3">
          <!-- Title Field -->
          <div>
            <label class="block text-xs font-medium text-n-slate-11 mb-1.5">
              {{ t('APPOINTMENTS.TITLE_LABEL') }} *
            </label>
            <input
              v-model="form.title"
              type="text"
              class="reset-base w-full rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
              :placeholder="t('APPOINTMENTS.TITLE_PLACEHOLDER')"
            />
          </div>

          <!-- Agendar para: o SDR marca na agenda do closer -->
          <div v-if="schedulableAgents.length">
            <label class="block text-xs font-medium text-n-slate-11 mb-1.5">
              {{ t('APPOINTMENTS.ASSIGNED_TO') }}
            </label>
            <select
              v-model="form.assigned_to_id"
              data-testid="appointment-assigned-to"
              class="reset-base w-full rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
            >
              <option
                v-for="agent in schedulableAgents"
                :key="agent.id"
                :value="agent.id"
              >
                {{
                  agent.google_connected
                    ? agent.name
                    : `${agent.name} — ${t('APPOINTMENTS.NO_GOOGLE_SHORT')}`
                }}
              </option>
            </select>
            <p
              v-if="assigneeWithoutGoogle"
              class="mt-1 text-xs text-n-slate-11"
            >
              {{
                t('APPOINTMENTS.NO_GOOGLE_HINT', {
                  name: selectedAgent.name,
                })
              }}
            </p>
          </div>

          <!-- Event Type Field -->
          <div>
            <label class="block text-xs font-medium text-n-slate-11 mb-1.5">
              {{ t('APPOINTMENTS.EVENT_TYPE') }}
            </label>
            <select
              v-model="form.event_type"
              class="reset-base w-full rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
            >
              <option value="event">{{ t('APPOINTMENTS.TYPE_EVENT') }}</option>
              <option value="task">{{ t('APPOINTMENTS.TYPE_TASK') }}</option>
            </select>
          </div>

          <!-- Date, Time and Duration Row -->
          <div class="grid grid-cols-3 gap-2">
            <div>
              <label class="block text-xs font-medium text-n-slate-11 mb-1.5">
                {{ t('APPOINTMENTS.DATE') }} *
              </label>
              <input
                v-model="form.date"
                type="date"
                class="reset-base w-full rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
              />
            </div>
            <div>
              <label class="block text-xs font-medium text-n-slate-11 mb-1.5">
                {{ t('APPOINTMENTS.TIME') }} *
              </label>
              <input
                v-model="form.time"
                type="time"
                class="reset-base w-full rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
              />
            </div>
            <div>
              <label class="block text-xs font-medium text-n-slate-11 mb-1.5">
                {{ t('APPOINTMENTS.DURATION') }}
              </label>
              <select
                v-model.number="form.duration_minutes"
                class="reset-base w-full rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
              >
                <option :value="15">15 {{ t('APPOINTMENTS.MINUTES') }}</option>
                <option :value="30">30 {{ t('APPOINTMENTS.MINUTES') }}</option>
                <option :value="45">45 {{ t('APPOINTMENTS.MINUTES') }}</option>
                <option :value="60">1 {{ t('APPOINTMENTS.HOUR') }}</option>
                <option :value="90">1.5 {{ t('APPOINTMENTS.HOURS') }}</option>
                <option :value="120">2 {{ t('APPOINTMENTS.HOURS') }}</option>
              </select>
            </div>
          </div>

          <!-- Location Field -->
          <div>
            <label class="block text-xs font-medium text-n-slate-11 mb-1.5">
              {{ t('APPOINTMENTS.LOCATION') }}
            </label>
            <input
              v-model="form.location"
              type="text"
              class="reset-base w-full rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
              :placeholder="t('APPOINTMENTS.LOCATION_PLACEHOLDER')"
            />
          </div>

          <!-- Meeting Link Provider -->
          <div>
            <label class="block text-xs font-medium text-n-slate-11 mb-1.5">
              {{ t('APPOINTMENTS.MEETING_LINK.LABEL') }}
            </label>
            <div class="flex gap-2">
              <button
                type="button"
                class="flex-1 px-3 py-2 text-sm rounded-lg border transition-all"
                :class="
                  form.meeting_provider === 'chatsuite'
                    ? 'border-n-teal-8 bg-n-teal-3 text-n-teal-11'
                    : 'border-n-weak text-n-slate-11 hover:text-n-slate-12'
                "
                @click="form.meeting_provider = 'chatsuite'"
              >
                {{ t('APPOINTMENTS.MEETING_LINK.CHATSUITE') }}
              </button>
              <button
                type="button"
                :disabled="!isGoogleConnected"
                class="flex-1 px-3 py-2 text-sm rounded-lg border transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                :class="
                  form.meeting_provider === 'google_meet'
                    ? 'border-n-teal-8 bg-n-teal-3 text-n-teal-11'
                    : 'border-n-weak text-n-slate-11 hover:text-n-slate-12'
                "
                @click="
                  isGoogleConnected && (form.meeting_provider = 'google_meet')
                "
              >
                {{ t('APPOINTMENTS.MEETING_LINK.GOOGLE_MEET') }}
              </button>
              <button
                type="button"
                class="flex-1 px-3 py-2 text-sm rounded-lg border transition-all"
                :class="
                  form.meeting_provider === 'presential'
                    ? 'border-n-teal-8 bg-n-teal-3 text-n-teal-11'
                    : 'border-n-weak text-n-slate-11 hover:text-n-slate-12'
                "
                @click="form.meeting_provider = 'presential'"
              >
                {{ t('APPOINTMENTS.MEETING_LINK.PRESENTIAL') }}
              </button>
            </div>
            <p
              v-if="
                !isGoogleConnected && form.meeting_provider !== 'presential'
              "
              class="mt-1 text-xs text-n-slate-10"
            >
              {{ t('APPOINTMENTS.MEETING_LINK.CONNECT_HINT') }}
            </p>
            <p
              v-if="form.meeting_provider === 'presential'"
              class="mt-1 text-xs text-n-slate-10"
            >
              {{ t('APPOINTMENTS.MEETING_LINK.PRESENTIAL_HINT') }}
            </p>
          </div>

          <!-- Funnel Selection Field (só quando o lead ainda não está em funil) -->
          <div v-if="!conversationFunnelId">
            <label class="block text-xs font-medium text-n-slate-11 mb-1.5">
              Funil
            </label>
            <select
              v-model.number="form.funnel_id"
              class="reset-base w-full rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
              :disabled="loadingFunnels"
            >
              <option :value="null">
                {{ t('KANBAN.SELECT_FUNNEL_OPTIONAL') }}
              </option>
              <option
                v-for="funnel in funnels"
                :key="funnel.id"
                :value="funnel.id"
              >
                {{ funnel.name }}
              </option>
            </select>
          </div>

          <!-- Description Field -->
          <div>
            <label class="block text-xs font-medium text-n-slate-11 mb-1.5">
              {{ t('APPOINTMENTS.DESCRIPTION') }}
            </label>
            <textarea
              v-model="form.description"
              rows="2"
              class="w-full rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand resize-none"
              :placeholder="t('APPOINTMENTS.DESCRIPTION_PLACEHOLDER')"
            />
          </div>

          <!-- Form Actions -->
          <div class="flex justify-end gap-2 pt-2">
            <Button
              size="sm"
              variant="faded"
              color="slate"
              :label="t('COMMON.CANCEL')"
              @click="toggleForm"
            />
            <Button
              size="sm"
              color="teal"
              :is-loading="saving"
              :label="
                editingAppointment ? t('COMMON.UPDATE') : t('COMMON.SAVE')
              "
              @click="saveAppointment"
            />
          </div>
        </div>
      </div>

      <!-- Appointments List -->
      <div v-if="upcomingAppointments.length > 0" class="space-y-3">
        <div
          v-for="appointment in upcomingAppointments"
          :key="appointment.id"
          class="bg-n-solid-1 border border-n-weak rounded-xl overflow-hidden hover:bg-n-alpha-1 transition-colors"
        >
          <div class="p-3 space-y-2">
            <!-- Header: AGENDAMENTO com Status e Actions -->
            <div
              class="flex justify-between items-center pb-2 border-b border-n-weak"
            >
              <div class="flex items-center gap-2">
                <span class="text-sm font-bold text-n-slate-12">AGENDAMENTO</span>
                <span
                  :class="getStatusClass(appointment.status)"
                  class="px-2 py-0.5 text-xs font-medium rounded"
                >
                  {{ getStatusLabel(appointment.status) }}
                </span>
              </div>
              <!-- Action Buttons -->
              <div class="flex items-center gap-1">
                <button
                  class="p-1 text-slate-400 hover:text-woot-600 dark:hover:text-woot-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded transition-all"
                  :title="t('COMMON.EDIT')"
                  @click="editAppointment(appointment)"
                >
                  <fluent-icon icon="edit" size="12" />
                </button>
                <button
                  class="p-1 text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-all"
                  :title="t('COMMON.DELETE')"
                  @click="deleteAppointment(appointment)"
                >
                  <fluent-icon icon="delete" size="12" />
                </button>
              </div>
            </div>

            <!-- Row 1: Motivo -->
            <div class="flex gap-2 text-xs">
              <span class="text-slate-500 dark:text-slate-400 font-medium">MOTIVO:</span>
              <span class="text-slate-700 dark:text-slate-300 font-semibold">{{
                appointment.title
              }}</span>
            </div>

            <!-- Row 2: Evento -->
            <div class="flex gap-2 text-xs">
              <span class="text-slate-500 dark:text-slate-400 font-medium">EVENTO:</span>
              <span
                class="text-slate-700 dark:text-slate-300 font-semibold uppercase"
              >
                {{
                  (appointment.event_type || appointment.appointment_type) ===
                  'event'
                    ? 'EVENTO'
                    : 'TAREFA'
                }}
              </span>
            </div>

            <!-- Row 3: Local -->
            <div class="flex gap-2 text-xs">
              <span class="text-slate-500 dark:text-slate-400 font-medium">LOCAL:</span>
              <span class="text-slate-700 dark:text-slate-300 font-semibold">
                {{ appointment.location || 'Não especificado' }}
              </span>
            </div>

            <!-- Row 4: Funil -->
            <div class="flex gap-2 text-xs">
              <span class="text-slate-500 dark:text-slate-400 font-medium">FUNIL:</span>
              <span class="text-blue-700 dark:text-blue-300 font-semibold">
                {{
                  appointment.funnel_id && getFunnelName(appointment.funnel_id)
                    ? getFunnelName(appointment.funnel_id)
                    : 'Não vinculado'
                }}
              </span>
            </div>

            <!-- Row 5: Data e Hora -->
            <div class="flex gap-2 text-xs">
              <span class="text-slate-500 dark:text-slate-400 font-medium">DATA E HORA:</span>
              <span
                class="text-slate-700 dark:text-slate-300 font-semibold flex items-center gap-1"
              >
                <fluent-icon icon="calendar" size="12" />
                {{
                  formatDate(
                    appointment.appointment_date || appointment.scheduled_at
                  )
                }}
              </span>
            </div>

            <!-- Row 6: Duração -->
            <div class="flex gap-2 text-xs">
              <span class="text-slate-500 dark:text-slate-400 font-medium">DURAÇÃO:</span>
              <span
                class="text-slate-700 dark:text-slate-300 font-semibold flex items-center gap-1"
              >
                <i class="ri-time-line text-xs" />
                {{ appointment.duration_minutes }} min
              </span>
            </div>

            <!-- Row 7: Descrição -->
            <div class="flex gap-2 text-xs">
              <span class="text-slate-500 dark:text-slate-400 font-medium">DESCRIÇÃO:</span>
              <span class="text-sm text-slate-600 dark:text-slate-400">
                {{ appointment.description || 'Sem descrição' }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else-if="!showForm" class="text-center py-8">
        <fluent-icon
          icon="calendar"
          size="32"
          class="text-slate-300 dark:text-slate-600 mb-2"
        />
        <p class="text-sm text-slate-500 dark:text-slate-400">
          {{ t('APPOINTMENTS.NO_APPOINTMENTS') }}
        </p>
        <p class="text-xs text-slate-400 dark:text-slate-500 mt-1">
          Clique no botão + para adicionar um agendamento
        </p>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.conversation-appointments {
  // Sem fundo branco, usando o padrão do Chatwoot
}
</style>
